/**
 * Ra'i (راعي) Portal Theme
 *
 * MUI theme tokens matching the Ra'i brand identity from the domain frontend.
 * Deep pasture green + desert amber palette, IBM Plex Sans / Arabic typography.
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

// ============================================================================
// Ra'i Color Palette
// ============================================================================

const primaryColors = {
  main: '#1B5E20',
  light: '#4CAF50',
  dark: '#0D3B12',
  contrastText: '#FFFFFF',
};

const secondaryColors = {
  main: '#F59E0B',
  light: '#FCD34D',
  dark: '#D97706',
  contrastText: '#FFFFFF',
};

const semanticColors = {
  success: { main: '#059669' },
  warning: { main: '#D97706' },
  error: { main: '#DC2626' },
  info: { main: '#2563EB' },
};

// ============================================================================
// Typography — IBM Plex Sans + Arabic
// ============================================================================

const typography = {
  fontFamily: '"IBM Plex Sans", "IBM Plex Sans Arabic", sans-serif',
  fontSize: 14,
  htmlFontSize: 16,
  h1: { fontSize: '2.5rem', fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.01562em' },
  h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.00833em' },
  h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0em' },
  h4: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.00735em' },
  h5: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0em' },
  h6: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.0075em', color: '#1B5E20' },
  subtitle1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.75, letterSpacing: '0.00938em' },
  subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.57, letterSpacing: '0.00714em' },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: '0.00938em' },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.43, letterSpacing: '0.01071em' },
  button: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.75, letterSpacing: '0.02857em', textTransform: 'none' as const },
  caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.66, letterSpacing: '0.03333em' },
  overline: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 2.66, letterSpacing: '0.08333em', textTransform: 'uppercase' as const },
};

// ============================================================================
// Component Overrides
// ============================================================================

const components: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: '#FAFAF5 !important',
        color: '#1A1A1A !important',
        colorScheme: 'light !important',
        scrollbarColor: '#c1c1c1 #FAFAF5',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 8, height: 8 },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': { borderRadius: 4, backgroundColor: '#c1c1c1', minHeight: 24 },
        '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a8a8a8' },
        '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': { borderRadius: 4, backgroundColor: '#FAFAF5' },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: { textTransform: 'none', borderRadius: 8, fontWeight: 600 },
      contained: {
        boxShadow: 'none',
        '&:hover': { boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)' },
      },
      containedPrimary: {
        backgroundColor: '#1B5E20',
        '&:hover': { backgroundColor: '#4CAF50' },
      },
      containedError: {
        backgroundColor: '#DC2626',
        '&:hover': { backgroundColor: '#EF4444' },
      },
      containedSecondary: {
        backgroundColor: '#F59E0B',
        color: '#1A1A1A',
        '&:hover': { backgroundColor: '#FCD34D' },
      },
      outlined: {
        borderWidth: 1,
        '&:hover': { borderWidth: 1 },
      },
      outlinedPrimary: {
        borderColor: '#1B5E20',
        color: '#1B5E20',
        '&:hover': { borderColor: '#4CAF50', backgroundColor: 'rgba(27, 94, 32, 0.04)' },
      },
    },
    defaultProps: { disableElevation: true },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
      },
    },
    defaultProps: { elevation: 1 },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: { padding: '16px 24px' },
      title: { fontSize: '1.125rem', fontWeight: 600 },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: { padding: '16px 24px', '&:last-child': { paddingBottom: 24 } },
    },
  },
  MuiTextField: { defaultProps: { variant: 'outlined', size: 'medium' } },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1B5E20', borderWidth: 2 },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: { fontSize: '0.875rem', '&.Mui-focused': { color: '#1B5E20' } },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 16, fontWeight: 500 },
      colorSuccess: { backgroundColor: '#059669', color: '#ffffff' },
      colorWarning: { backgroundColor: '#F59E0B', color: '#1A1A1A' },
      colorError: { backgroundColor: '#DC2626', color: '#ffffff' },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
      rounded: { borderRadius: 12 },
      elevation1: { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' },
      elevation2: { boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)' },
      elevation3: { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)' },
    },
  },
  MuiDialog: {
    styleOverrides: { paper: { borderRadius: 12 } },
  },
  MuiDialogTitle: {
    styleOverrides: { root: { padding: '16px 24px', fontWeight: 600, color: '#1A1A1A' } },
  },
  MuiDialogContent: {
    styleOverrides: { root: { padding: '16px 24px' } },
  },
  MuiDialogActions: {
    styleOverrides: { root: { padding: '12px 24px' } },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          fontWeight: 600,
          fontSize: '0.875rem',
          backgroundColor: '#1B5E20',
          color: '#FFFFFF',
        },
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': { backgroundColor: '#FFFFFF !important' },
        '& .MuiTableRow-root:nth-of-type(even)': { backgroundColor: '#FAFAF5 !important' },
        '& .MuiTableRow-root:hover': { backgroundColor: '#E8F5E9 !important' },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: { root: { borderBottom: '1px solid #E5E7EB' } },
  },
  MuiTablePagination: {
    styleOverrides: { root: { borderTop: '1px solid #E5E7EB' } },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        color: '#FFFFFF !important',
        '&.Mui-active': { color: '#FFFFFF !important' },
        '& .MuiTableSortLabel-icon': { color: '#FFFFFF !important' },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        minWidth: 'auto',
        '&.Mui-selected': { color: '#1B5E20' },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 3, borderTopLeftRadius: 3, borderTopRightRadius: 3, backgroundColor: '#1B5E20' },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 10 },
      standardSuccess: { backgroundColor: 'rgba(5, 150, 105, 0.1)' },
      standardError: { backgroundColor: 'rgba(220, 38, 38, 0.1)' },
      standardWarning: { backgroundColor: 'rgba(217, 119, 6, 0.1)' },
      standardInfo: { backgroundColor: 'rgba(37, 99, 235, 0.1)' },
    },
  },
  MuiTooltip: {
    styleOverrides: { tooltip: { borderRadius: 4, fontSize: '0.75rem' } },
  },
  MuiDrawer: {
    styleOverrides: { paper: { borderRight: '1px solid', borderColor: 'divider' } },
  },
  MuiAppBar: {
    styleOverrides: { root: { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)' } },
    defaultProps: { elevation: 0 },
  },
  MuiLinearProgress: {
    styleOverrides: { root: { borderRadius: 4, height: 6 } },
  },
  MuiSkeleton: {
    styleOverrides: { root: { borderRadius: 4 } },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      root: { fontSize: '0.875rem' },
      separator: { color: '#6B7280' },
    },
  },
  MuiListItemButton: {
    styleOverrides: { root: { borderRadius: 4 } },
  },
  MuiListItemIcon: {
    styleOverrides: { root: { minWidth: 40 } },
  },
  MuiMenu: {
    styleOverrides: { paper: { borderRadius: 10, boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)' } },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: { '& .MuiPaper-root': { borderRadius: 10 } },
    },
  },
};

// ============================================================================
// Theme
// ============================================================================

export const livestockTheme = createTheme({
  palette: {
    mode: 'light',
    primary: primaryColors,
    secondary: secondaryColors,
    ...semanticColors,
    background: {
      default: '#FAFAF5',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#6B7280',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: '#E5E7EB',
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
    },
  },
  typography,
  shape: { borderRadius: 10 },
  spacing: 8,
  components,
});

export { MODULE_CONFIGS, MODULE_LIST } from './moduleColors';
export type { ModuleId, ModuleConfig } from './moduleColors';
